import { useEffect } from "react";

import { Icon, ParagraphSVG } from "@newsela/angelou";

import ContentLevel from "@/types/models/ContentLevel.interface";
import ActionPopOutOption from "@/view/ActionPopOutOption/ActionPopOutOption.js";
import useReadAloudMode from "@/view/ReadAloudControls/useReadAloudMode";
import SecondaryActionButton from "@/view/SecondaryActionsToolbar/SecondaryActionButton";
import useAccessibilityControlsContext from "@/view/utils/useAccessibilityControlsContext";
import { iconCss } from "./ParagraphCountAction.style";

interface ParagraphCountActionProps {
  activeSubMenu: string | null | undefined;
  currentLevel: ContentLevel | undefined;
  disabled: boolean;
  hoverLabel: string;
  isMobile: boolean | undefined;
  showDropdownParagraphCount: boolean;
  showToolbarParagraphCount: boolean;
}

const ParagraphCountAction = ({
  activeSubMenu,
  currentLevel,
  disabled,
  hoverLabel,
  isMobile = false,
  showDropdownParagraphCount,
  showToolbarParagraphCount,
}: ParagraphCountActionProps) => {
  const { showParagraphCount, setShowParagraphCount } =
    useAccessibilityControlsContext();
  const { isReadAloudModeActive } = useReadAloudMode();

  const isActionVisible =
    showDropdownParagraphCount || showToolbarParagraphCount;

  useEffect(() => {
    if (isReadAloudModeActive) setShowParagraphCount(false);
  }, [isReadAloudModeActive]);

  if ((isMobile && !!activeSubMenu) || !isActionVisible) return null;

  const eventProperties = {
    objectType: "Content",
    actionName: "Interact",
    streamProperties: {
      content_type: "Article",
      description: showParagraphCount
        ? "Paragraph count hidden"
        : "Paragraph count shown",
      content_id: currentLevel?.id,
    },
  };

  const onClick = () =>
    setShowParagraphCount(!showParagraphCount && !isReadAloudModeActive);

  const actionName = "Count";
  const ariaProps = {
    "aria-label": "Paragraph count",
    "aria-pressed": showParagraphCount,
    "aria-disabled": isReadAloudModeActive,
  };
  const qaSelector = showParagraphCount
    ? "hide-paragraph-count"
    : "show-paragraph-count";
  const icon = (
    <Icon __cssFor={{ root: iconCss }} SvgComponent={ParagraphSVG} />
  );

  if (showDropdownParagraphCount) {
    return (
      <ActionPopOutOption
        // @ts-expect-error  Property 'dataProps' does not exist on type 'IntrinsicAttributes & RefAttributes<any>'.
        dataProps={{ "data-qa-selector": `dropdown-${qaSelector}` }}
        eventProperties={eventProperties}
        icon={icon}
        onClick={onClick}
        title={actionName}
        disabled={disabled}
      />
    );
  }

  return (
    <SecondaryActionButton
      ariaProps={ariaProps}
      dataProps={{ "data-qa-selector": qaSelector }}
      disabled={disabled}
      eventProperties={eventProperties}
      hoverLabel={hoverLabel}
      icon={icon}
      isActive={showParagraphCount}
      onClick={onClick}
      title={actionName}
    />
  );
};

export default ParagraphCountAction;
